import React from "react";
import ReactDOM from "react-dom";
import {
  BrickWrapper,
  UpdatingElement,
  property,
  event,
  EventEmitter,
  method,
} from "@next-core/brick-kit";
import { WorkbenchDataTree } from "./WorkbenchDataTree";
import { WorkbenchActionsContext } from "../shared/workbench/WorkbenchActionsContext";
import {
  ActionClickDetail,
  WorkbenchNodeData,
  WorkbenchTreeAction,
} from "../shared/workbench/interfaces";
import { dropEmitProps } from "../shared/workbench/WorkbenchTree";
import { BuilderProvider } from "@next-core/editor-bricks-helper";

export interface WorkbenchDataTreeProps {
  nodes?: WorkbenchNodeData[];
  actions?: WorkbenchTreeAction[];
  actionsHidden?: boolean;
  placeholder?: string;
  isTransformName?: boolean;
  searchPlaceholder?: string;
  noSearch?: boolean;
  activeKey?: string | number;
  showMatchedNodeOnly?: boolean;
  matchNodeDataFields?: string | string[];
  showChildrenIfMatchParent?: boolean;
  fixedActionsFor?: Record<string, unknown> | Record<string, unknown>[];
  collapsible?: boolean;
  allowDrag?: boolean;
  allowDragToRoot?: boolean;
  allowDragToInside?: boolean;
  nodeKey?: string;
  showLine?: boolean;
  disabledNodeSuffixClick?: boolean;
  onActionClick?: (event: CustomEvent) => void;
  onNodeClick?: (event: CustomEvent) => void;
  onNodeDrop?: (event: CustomEvent) => void;
  onContextMenu?: (event: CustomEvent) => void;
  onNodenameSuffixClick?: (event: CustomEvent) => void;
}

export interface WorkbenchDataTreeElementProps {
  nodes?: WorkbenchNodeData[];
  actions?: WorkbenchTreeAction[];
  actionsHidden?: boolean;
  placeholder?: string;
  isTransformName?: boolean;
  searchPlaceholder?: string;
  noSearch?: boolean;
  activeKey?: string | number;
  showMatchedNodeOnly?: boolean;
  matchNodeDataFields?: string | string[];
  showChildrenIfMatchParent?: boolean;
  fixedActionsFor?: Record<string, unknown> | Record<string, unknown>[];
  collapsible?: boolean;
  allowDrag?: boolean;
  allowDragToRoot?: boolean;
  allowDragToInside?: boolean;
  nodeKey?: string;
  showLine?: boolean;
  disabledNodeSuffixClick?: boolean;
  onActionClick?: (event: CustomEvent<ActionClickDetail>) => void;
  onNodeClick?: (event: CustomEvent<unknown>) => void;
  onNodeDrop?: (event: CustomEvent<dropEmitProps>) => void;
  onContextMenu?: (event: CustomEvent<unknown>) => void;
  onNodenameSuffixClick?: (event: CustomEvent<unknown>) => void;
}

/**
 * @id next-builder.workbench-data-tree
 * @author SheRunFeng
 * @history
 * 1.x.0: 新增构件 `next-builder.workbench-data-tree`
 * @docKind brick
 * @noInheritDoc
 */
export class WorkbenchDataTreeElement extends UpdatingElement implements WorkbenchDataTreeProps {
  @property({ attribute: false })
  nodes: WorkbenchNodeData[];

  @property({ attribute: false })
  actions: WorkbenchTreeAction[];

  @property({ type: Boolean })
  actionsHidden: boolean;

  @property()
  placeholder: string;

  @property({ type: Boolean })
  isTransformName: boolean;

  @property()
  searchPlaceholder: string;

  @property({ type: Boolean })
  noSearch: boolean;

  @property({ attribute: false })
  activeKey: string | number;

  @property({ type: Boolean })
  showMatchedNodeOnly: boolean;

  @property({ attribute: false })
  matchNodeDataFields: string | string[];

  @property({ type: Boolean })
  showChildrenIfMatchParent: boolean;

  @property({ attribute: false })
  fixedActionsFor: Record<string, unknown> | Record<string, unknown>[];

  @property({ type: Boolean })
  collapsible: boolean;

  @property({ type: Boolean })
  allowDrag: boolean;

  @property({ type: Boolean })
  allowDragToRoot: boolean;

  @property({ type: Boolean })
  allowDragToInside: boolean;

  @property({ type: String })
  nodeKey: string;

  @property({ type: Boolean })
  showLine: boolean;

  @property({ type: Boolean })
  disabledNodeSuffixClick: boolean;

  @event({ type: "action.click" })
  private _actionClickEvent: EventEmitter<ActionClickDetail>;

  private _handleActionClick = (detail: ActionClickDetail): void => {
    this._actionClickEvent.emit(detail);
  };

  @event({ type: "node.click" })
  private _nodeClickEvent: EventEmitter<unknown>;

  private _nodeClickFactory = (node: WorkbenchNodeData) => () => {
    this._nodeClickEvent.emit(node.data);
  };

  @event({ type: "node.drop" })
  private _nodeDropEvent: EventEmitter<dropEmitProps>;

  private _handleNodeDrop = (detail: dropEmitProps): void => {
    this._nodeDropEvent.emit(detail);
  };

  @event({ type: "context.menu" })
  private _nodeContextMenuEvent: EventEmitter<unknown>;

  private _contextMenuFactory =
    (node: WorkbenchNodeData) => (e: React.MouseEvent) => {
      e.preventDefault();
      this._nodeContextMenuEvent.emit({
        active: true,
        node: node?.data,
        x: e.clientX,
        y: e.clientY,
      });
    };

  @event({ type: "nodeName.suffix.click" })
  private _nodeNameSuffixClickEvent: EventEmitter<unknown>;

  private _activeNode: HTMLElement;

  @method()
  scrollActiveNodeIntoView(): void {
    this._activeNode?.scrollIntoView({
      block: "center",
      behavior: "smooth",
      inline: "center",
    });
  }
  private _nodeNameSuffixClick = (node: WorkbenchNodeData): void => {
    this._nodeNameSuffixClickEvent.emit(node.data);
  };
  private _handleNodeActive = (node: HTMLElement): void => {
    this._activeNode = node;
  };

  connectedCallback(): void {
    // Don't override user's style settings.
    // istanbul ignore else
    if (!this.style.display) {
      this.style.display = "block";
    }
    this._render();
  }

  disconnectedCallback(): void {
    ReactDOM.unmountComponentAtNode(this);
  }

  protected _render(): void {
    // istanbul ignore else
    if (this.isConnected) {
      ReactDOM.render(
        <BrickWrapper>
          <BuilderProvider>
            <WorkbenchActionsContext.Provider
              value={{
                actions: this.actions,
                actionsHidden: this.actionsHidden,
                onActionClick: this._handleActionClick,
              }}
            >
              <WorkbenchDataTree
                trees={this.nodes}
                allowDrag={this.allowDrag}
                allowDragToRoot={this.allowDragToRoot}
                allowDragToInside={this.allowDragToInside}
                placeholder={this.placeholder}
                searchPlaceholder={this.searchPlaceholder}
                noSearch={this.noSearch}
                dropEmit={this._handleNodeDrop}
                activeKey={this.activeKey}
                nodeKey={this.nodeKey}
                onNodeActive={this._handleNodeActive}
                disabledNodeSuffixClick={this.disabledNodeSuffixClick}
                clickFactory={this._nodeClickFactory}
                contextMenuFactory={this._contextMenuFactory}
                matchNodeDataFields={this.matchNodeDataFields}
                onNodeNameSuffixClick={this._nodeNameSuffixClick}
                showLine={this.showLine}
                showChildrenIfMatchParent={this.showChildrenIfMatchParent}
              />
            </WorkbenchActionsContext.Provider>
          </BuilderProvider>
        </BrickWrapper>,
        this
      );
    }
  }
}

customElements.define(
  "next-builder.workbench-data-tree",
  WorkbenchDataTreeElement
);
